import type {
    CommonProperties,
    CoreAnalyticsOptions,
    SharedCommonProperties,
    AccountSpecificCommonProperties,
    MailboxCollectorMappingAddon,
} from 'owa-analytics-types';
import type { AccountKey } from 'owa-client-types';
import { getSessionId } from 'owa-config/lib/getSessionId';
import { getHostValue } from 'owa-config/lib/getHostValue';
import { getNativeHostVersion, getEnhancedHostDeviceId } from 'owa-config/lib/enhancedHost';
import { getAnalyticsAddon } from 'owa-analytics-addons';
/* eslint-disable-next-line @typescript-eslint/no-restricted-imports  -- (https://aka.ms/OWALintWiki)
 * Deprecating getPhysicalRing.ts
 *	> 'getPhysicalRing' import from 'owa-metatags' is restricted. This value is resolved in ECS as a filter. Please create a feature flight if possible.
 *	> 'getVariantEnvironment' import from 'owa-metatags' is restricted. This value is resolved in ECS as a filter. Please create a feature flight if possible. */
import { getPhysicalRing, getVariantEnvironment } from 'owa-metatags';
import getUserType from 'owa-service/lib/getUserType';
import type WebSessionType from 'owa-service/lib/contract/WebSessionType';
import { getThreadName } from 'owa-thread-config';
import { getCachedSettings } from './getCachedSettings';
import { trace } from 'owa-trace';
import getAutomationType from '../utils/getAutomationType';

const commonPropertiesCache = new Map<AccountKey | 'globalAccount', CommonProperties>();

export async function getCommonProperties(
    analyticsOptions: CoreAnalyticsOptions,
    accountKey: AccountKey | 'globalAccount'
): Promise<CommonProperties> {
    const cached = commonPropertiesCache.get(accountKey);
    if (cached) {
        return {
            ...cached,
            AutomationType: getAutomationType(),
        };
    }

    const { sessionInfo, accountScopeSessionSettings, opxSessionInfo, userConfiguration } =
        getCachedSettings(accountKey) ?? {};
    const accountInfo = getAnalyticsAddon<MailboxCollectorMappingAddon>('MailboxCollectorMapping')
        .executeNow()
        ?.getMailboxCollectorAccountInfo(accountKey);

    const sessionSettings = userConfiguration?.SessionSettings;

    const sharedProperties: SharedCommonProperties = {
        AutomationType: getAutomationType(),
        ClientId: sessionInfo?.clientId,
        DeviceId: getEnhancedHostDeviceId(),
        Host: getHostValue(),
        HostVersion: getNativeHostVersion(),
        HostedScenario: opxSessionInfo?.hostedScenario,
        Layout: sessionInfo?.layout,
        'Session.Id': getSessionId(),
        Thread: getThreadName(),
    };

    const accountSpecificProperties: AccountSpecificCommonProperties = {
        BEServer: sessionInfo?.backend,
        Forest: sessionInfo?.forest,
        MailboxType: accountInfo?.mailboxType,
        MBXGuid: accountScopeSessionSettings?.SessionSettings?.MailboxGuid,
        MultiAccountType: accountInfo?.multiAccountType,
        PhysicalRing: accountInfo?.isGlobalAccount
            ? getPhysicalRing()
            : 'UnavailableForSecondaryAccounts',
        Puid: sessionSettings?.UserPuid,
        Ring: sessionInfo?.logicalRing,
        TenantGuid: sessionSettings?.ExternalDirectoryTenantGuid,
        UserType: getUserType(accountKey, accountScopeSessionSettings, !!sessionInfo?.isEdu),
        VariantEnv: accountInfo?.isGlobalAccount
            ? getVariantEnvironment()
            : 'UnavailableForSecondaryAccounts',
    };

    const commonProperties = {
        ...sharedProperties,
        ...accountSpecificProperties,
    };

    if (opxSessionInfo) {
        commonProperties['HostedScenario'] = opxSessionInfo.hostedScenario;
    }

    if (!!analyticsOptions?.shouldIncludeUserInfoId) {
        commonProperties['UserInfo.Id'] = sessionSettings?.UserPuid;
        commonProperties['UserInfo.IdType'] =
            accountScopeSessionSettings &&
            accountScopeSessionSettings?.SessionSettings?.WebSessionType !== (0).toString()
                ? 'MSAPUID'
                : 'OrgIdPuid';
    }

    commonPropertiesCache.set(accountKey, commonProperties);

    return commonProperties;
}

export function updateCommonPropertiesCache(
    commonProperties: CommonProperties,
    accountKey: AccountKey | 'globalAccount'
): void {
    trace.info('Updating common properties cache', 'analytics');
    commonPropertiesCache.set(accountKey, commonProperties);
}
